import type { TaskGroup, TimberProject } from '../types';

const ESTIMATING_GROUP_NAME = 'estimating';

/** Canonical Project Hub table / single-project gantt section order. */
export const PROJECT_TASK_TABLE_GROUP_ORDER = [
  'Client Communication',
  'Estimating',
  'Design',
  'Procurement',
  'Production',
  'Installation',
] as const;

export function isEstimatingTaskGroup(group: Pick<TaskGroup, 'name'>): boolean {
  return group.name.trim().toLowerCase() === ESTIMATING_GROUP_NAME;
}

function normGroupName(name: string): string {
  return name.trim().toLowerCase();
}

/**
 * Lower rank = higher on the project table. Unknown groups sort after the
 * standard sections, then by Firestore `position`.
 */
export function projectTaskGroupSectionRank(name: string): number {
  const n = normGroupName(name);
  if (!n) return 900;
  if (n.includes('client') && n.includes('communication')) return 0;
  if (n.includes('client comm')) return 0;
  if (n === 'estimating' || n.includes('estimating')) return 10;
  if (n.includes('preconstruction') || n === 'precon') return 15;
  if (n === 'design') return 20;
  if (n.includes('procurement')) return 30;
  if (n === 'production' || n.includes('production')) return 40;
  if (
    n.includes('installation')
    || n.includes('install')
    || n.includes('delivery')
    || n.includes('shipment')
  ) {
    return 50;
  }
  return 900;
}

/** @deprecated Table order no longer depends on project phase. */
export function shouldPinEstimatingGroupFirst(
  project: Pick<TimberProject, 'phase' | 'status'> | null | undefined,
): boolean {
  if (!project || project.status === 'completed') return false;
  const phase = project.phase ?? 'contracted';
  return phase === 'preconstruction' || phase === 'estimating' || phase === 'contracted';
}

export function sortTaskGroupsForProject<T extends TaskGroup>(
  groups: T[],
  _project?: Pick<TimberProject, 'phase' | 'status'> | null,
): T[] {
  return [...groups].sort((a, b) => {
    const ra = projectTaskGroupSectionRank(a.name);
    const rb = projectTaskGroupSectionRank(b.name);
    if (ra !== rb) return ra - rb;
    return (a.position || 0) - (b.position || 0);
  });
}

/** Default precon task groups — Estimating first. */
export const DEFAULT_PRECON_TASK_GROUPS = [
  { name: 'Estimating', color: '#F59E0B' },
  { name: 'Design', color: '#8B5CF6' },
  { name: 'Client Communication', color: '#0891B2' },
] as const;

/**
 * Groups every custom under-contract project needs so onboarding checklist
 * departments (Design / Procurement / Production / Installation) resolve.
 * Precon-originated jobs only start with Estimating + Design + Client
 * Communication — Production and Installation must be added on contract.
 */
export const DEFAULT_CONTRACTED_TASK_GROUPS = [
  { name: 'Design', color: '#9F7AEA', department: 'design' as const },
  { name: 'Procurement', color: '#4299E1', department: 'procurement' as const },
  { name: 'Production', color: '#ED8936', department: 'production' as const },
  { name: 'Installation', color: '#38B2AC', department: 'installation' as const },
] as const;
